import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ExpiryInfoBar from '../../components/ExpiryInfoBar';
import { PaymentMethods } from '../../components/payment-method/payment-card';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import { useApiCache } from '../../context/api-context';
import { ayeApi } from '../../core';
import { ICommonResponse } from '../../core/api/http-methods/common-response.type';
import { PaymentConsents } from '../../core/api/http-methods/get-payment-methods';
import { DUITNOW_METHODS } from '../../constants/duitnow.const';
import type { PaymentConsent } from '../../types/payment';
import AddPaymentMethodDialog from './components/AddPaymentMethodDialog';
import ClosePopupConfirmationPopup from './components/ClosePopupConfirmationPopup';
import FailedPopup from './components/FailedPopup';
import Greeting from './components/Greeting';
import NoPaymentMethodCard from './components/NoPaymentMethodCard';
import RecentActivities from './components/RecentActivities/index';
import ReferralDialog from './components/ReferralDialog';
import RewardPointsSection from './components/RewardPointsSection';
import RewardsSection from './components/RewardsSection';
import SucceedPopup from './components/SucceedPopup';
import { useAddPaymentFlow } from './hooks/useAddPaymentFlow';

const IS_EWALLET_ENABLED =
  process.env.REACT_APP_FEATURE_EWALLET_ENABLED === 'true';
const IS_DUITNOW_ENABLED =
  process.env.REACT_APP_FEATURE_DUITNOW_ENABLED === 'true';
const IS_DUITNOW_ID_ENABLED =
  process.env.REACT_APP_FEATURE_DUITNOW_ID_ENABLED === 'true';
const IS_REWARD_POINTS_ENABLED =
  process.env.REACT_APP_FEATURE_AYEFACE_POINTS_VISIBLE === 'true';

const Home: React.FC = () => {
  const { state: apiCache, dispatch } = useApiCache();
  // The list the carousel shows; undefined until the first load, so the
  // first-method card never shows while the carousel skeleton should.
  const shownConsents = (
    apiCache.cache.payment_consents as PaymentConsents | undefined
  )?.payment_consents;
  const hasNoPaymentMethod = shownConsents?.length === 0;
  const [paymentMethods, setPaymentMethods] = useState<
    PaymentConsent[] | undefined
  >();
  const [succeed, setSucceed] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [recentlyAddedPaymentMethod] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  // E3: bumped on visibilitychange while Screen 4 is open, to force an immediate poll.
  const [pollPulse, setPollPulse] = useState(0);

  // C1: `useApp().paymentMethods()` (usePortalHook.tsx:309-324) only ever updates this
  // component's own local `paymentMethods` state — used by ExpiryInfoBar and E3's pending
  // check. It does NOT touch the shared apiCache key 'payment_consents' that the visible
  // carousel (<PaymentMethods />, via useApiFetch({ cacheKey: 'payment_consents' }) in
  // components/payment-method/payment-card.tsx) actually reads. So calling it after E1's close,
  // E12's ACTIVE/declined, or E13's stale-consent case never updated what the user sees — the
  // new PENDING/ACTIVE card only appeared after a full reload. Fetch directly and write BOTH:
  // the shared cache (same pattern as components/settings/user-profile.tsx:105,160) and this
  // page's own local state, from the same response — one call, no second redundant fetch.
  const refreshPaymentMethods = useCallback(async () => {
    const res = (await ayeApi.getPaymentMethods(
      {}
    )) as unknown as ICommonResponse<PaymentConsents>;
    if (!res?.success) return;
    dispatch({ type: 'SET_DATA', key: 'payment_consents', data: res.data });
    setPaymentMethods(res.data.payment_consents);
  }, [dispatch]);

  const flow = useAddPaymentFlow({
    isEWalletEnabled: IS_EWALLET_ENABLED,
    isDuitNowIdEnabled: IS_DUITNOW_ID_ENABLED,
    // E13: a consent created by a stale/cancelled request still needs the list refreshed so
    // its PENDING card appears without the user having to do anything.
    onConsentPending: refreshPaymentMethods
  });

  useEffect(() => {
    refreshPaymentMethods();
  }, [refreshPaymentMethods]);

  const handleOpen = () => {
    flow.reset();
    setOpen(true);
  };

  // Real close: used by every path that actually dismisses the dialog (confirmed close, the
  // post-result popups' close/finish, and E1's fast-close). Also resets the flow so a stale
  // in-flight request from the closed attempt can no longer navigate or mutate visible state
  // (E13's generation guard, bumped inside flow.reset()).
  const closeDialog = useCallback(() => {
    setOpen(false);
    setShowConfirmation(false);
    setIsSubmitted(false);
    flow.reset();
    // `flow` is a fresh object every render, so this redefines `closeDialog` on every render
    // too — harmless, since it's only ever read at click/event time, never diffed for memoization.
  }, [flow]);

  const onClose = () => {
    // E1: closing on the approval-pending screen (X / Escape / outside click / Done) is always
    // safe and immediate — the DuitNow request stays valid at the bank no matter what the UI
    // does, so no "you'll lose your progress" confirmation applies here. Polling stops because
    // DialogDuitNowApproval unmounts; refresh the list so the new PENDING card is visible.
    if (flow.step === 'duitnow-approval') {
      closeDialog();
      refreshPaymentMethods();
      return;
    }

    if (!isSubmitted) {
      if (showConfirmation) {
        closeDialog();
      } else {
        setShowConfirmation(true);
      }
    } else {
      closeDialog();
    }
  };

  const tryAgain = () => {
    setIsSubmitted(false);
    flow.reset();
  };

  // `flow` is a fresh object every render (useAddPaymentFlow returns a new object literal each
  // call). A previous version of this effect listed `flow` as a dependency so it could call
  // `flow.resumeDuitNowApproval`. That is a real bug, not a harmless one: it re-runs this
  // effect on every render regardless of whether `searchParams` changed, and — because the
  // effect body itself calls `setSearchParams` — that can chain into a render loop that never
  // settles (confirmed: hundreds of `history.replaceState` calls/second, the result popup never
  // showing). Read `flow` through a ref, updated during render (always current, no dependency),
  // so this effect depends only on the two things that should actually retrigger it.
  const flowRef = useRef(flow);
  flowRef.current = flow;

  useEffect(() => {
    const status = searchParams.get('status');
    const duitNowRef = searchParams.get('duitnow_ref');

    if (status) {
      // Set the success/failure state
      if (status === 'success') {
        setSucceed(true);
      } else if (status === 'failed') {
        setSucceed(false);
      }

      // Open the dialog and show the result
      setIsSubmitted(true);
      setOpen(true);

      // Remove the status param from URL while keeping other params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('status');
      setSearchParams(newParams, { replace: true });
    } else if (searchParams.get('add_payment') === '1') {
      // Deep link from the checkout exit: open the add-payment dialog at its first step.
      flowRef.current.reset();
      setIsSubmitted(false);
      setOpen(true);

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('add_payment');
      setSearchParams(newParams, { replace: true });
    } else if (duitNowRef) {
      // Reopen the dialog on Screen 4 (in-dialog approval polling) for a pending consent.
      flowRef.current.resumeDuitNowApproval(duitNowRef);
      setOpen(true);

      // Remove the duitnow_ref param from URL while keeping other params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('duitnow_ref');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // E3: the user switches to their banking app and comes back. On the tab becoming visible:
  //  - dialog open on Screen 4: poll immediately instead of waiting out the rest of the 3s tick.
  //  - dialog not on Screen 4 but the list has a PENDING DuitNow row: re-fetch the list quietly
  //    (no popup) so an approval that happened meanwhile shows up without a manual refresh.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (open && flow.step === 'duitnow-approval') {
        setPollPulse((p) => p + 1);
        return;
      }
      const hasPendingDuitNow = paymentMethods?.some(
        (m) =>
          (DUITNOW_METHODS as readonly string[]).includes(m.method) &&
          m.status === 'PENDING'
      );
      if (hasPendingDuitNow) refreshPaymentMethods();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [open, flow.step, paymentMethods, refreshPaymentMethods]);

  return (
    <>
      <div className="">
        <Greeting />

        {/* Info bar for payments which are about to expire soon or expired */}
        {paymentMethods && <ExpiryInfoBar paymentMethods={paymentMethods} />}

        {hasNoPaymentMethod ? (
          <NoPaymentMethodCard onAdd={() => handleOpen()} />
        ) : (
          <>
            <div className="flex flex-col gap-y-4 px-4 sm:px-6 md:flex-row md:justify-between lg:px-8">
              <h2 className="text-lg font-bold leading-6 text-gray-900 md:text-xl">
                Payment Methods Overview
              </h2>
              <Button
                onClick={() => handleOpen()}
                className={
                  'max-w-[14rem] rounded-full bg-[#5CB891] px-2 text-primary-foreground hover:bg-[#5CB891]/80'
                }
              >
                <span className="text-sm font-semibold">
                  Add New Payment Method
                </span>
              </Button>
            </div>
            <PaymentMethods />
          </>
        )}

        {IS_REWARD_POINTS_ENABLED && <RewardPointsSection />}

        <RewardsSection />

        <RecentActivities />

        <AddPaymentMethodDialog
          open={open && !isSubmitted}
          onClose={onClose}
          isEWalletEnabled={IS_EWALLET_ENABLED}
          isDuitNowEnabled={IS_DUITNOW_ENABLED}
          isDuitNowIdEnabled={IS_DUITNOW_ID_ENABLED}
          refreshPaymentMethods={refreshPaymentMethods}
          pollPulse={pollPulse}
          {...flow}
        />

        {isSubmitted &&
          (succeed ? (
            <Dialog open={isSubmitted && succeed} onOpenChange={closeDialog}>
              <DialogContent className="max-w-[25rem] rounded-3xl sm:max-w-[26rem]">
                <DialogHeader>
                  <DialogTitle className="sr-only">Payment Added</DialogTitle>
                </DialogHeader>
                <SucceedPopup
                  onClose={closeDialog}
                  addedPaymentMethod={recentlyAddedPaymentMethod}
                />
              </DialogContent>
            </Dialog>
          ) : (
            <Dialog open={isSubmitted && !succeed} onOpenChange={closeDialog}>
              <DialogContent className="max-w-[25rem] rounded-3xl sm:max-w-[26rem]">
                <DialogHeader>
                  <DialogTitle className="sr-only">Payment Failed</DialogTitle>
                </DialogHeader>
                <FailedPopup
                  onClose={closeDialog}
                  tryAgain={tryAgain}
                  message={flow.errorMessage}
                  variant={flow.errorVariant}
                />
              </DialogContent>
            </Dialog>
          ))}

        <ClosePopupConfirmationPopup
          open={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={closeDialog}
        />
      </div>

      <ReferralDialog />
    </>
  );
};

export default Home;
